import { StyledMobileMenu } from './style';
import { useIntl } from 'react-intl';
import { Button, Icon } from 'ui';
import { useNavigate } from 'react-router-dom';
import { MobileMenuDrawer } from 'ui/Drawer/Drawers/MobileMenuDrawer';
import { useState } from 'react';
import { Menu, message } from 'antd';
import { MyProfileDrawer } from 'ui/Drawer/Drawers/MobileMyProfileDrawer';
import MobileProfielOptions from '../MobileProfielOptions/MobileProfielOptions';
import { useAuth } from 'hooks/useAuth';
import useQueryApiClient from 'utils/useQueryApiClient';
import { useFilterDispatch, useFilterState } from 'public/context';
import { headerLinks } from 'config/config';
import { useCartState } from 'public/context/CartContext';
import { FarmProduct } from 'public/context/CartContext';
import PublicRoute, { publicPath } from 'enums/publicRoutes';
import useMarketType from 'utils/useMarketType';

const MobileMenu = ({ farmProducts }: { farmProducts: FarmProduct[] }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openProfile, setOpenProfile] = useState(false);
  const [selectedKey, setSelectedKey] = useState<null | string>(null);
  const intl = useIntl();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isOpenMobileFilter } = useFilterState();
  const dispatchFilter = useFilterDispatch();
  const { priceTotal } = useCartState();
  const { isEmarket } = useMarketType();

  const { data: orderCount } = useQueryApiClient({
    request: {
      url: '/api/v2/user/order',
      data: {
        count: true,
      },
      disableOnMount: !user?.authenticated,
    },
  });

  const handleButtonClick = (key: string, action: () => void) => {
    if (selectedKey === key) {
      setSelectedKey(null);
    } else {
      setSelectedKey(key);
    }
    action();
    setIsMenuOpen(false);
  };

  const sharedItems = [
    {
      key: 'home',
      label: intl.formatMessage({ id: 'breadcrumbs.home' }),
      onClick: () => handleButtonClick('home', () => navigate(PublicRoute.HOME)),
    },
    {
      key: 'main',
      label: intl.formatMessage({ id: 'navigation.main' }),
      onClick: () =>
        handleButtonClick('main', () => {
          window.location.href = headerLinks['main'];
        }),
    },
    {
      key: 'current_events',
      label: intl.formatMessage({ id: 'navigation.current_events' }),
      onClick: () =>
        handleButtonClick('current_events', () => {
          window.location.href = headerLinks['current_events'];
        }),
    },
    {
      key: 'contests',
      label: intl.formatMessage({ id: 'navigation.contests' }),
      onClick: () =>
        handleButtonClick('contests', () => {
          window.location.href = headerLinks['contests'];
        }),
    },
    {
      key: 'recipes',
      label: intl.formatMessage({ id: 'navigation.recipes' }),
      onClick: () =>
        handleButtonClick('recipes', () => {
          window.location.href = headerLinks['recipes'];
        }),
    },
    {
      key: 'events',
      label: intl.formatMessage({ id: 'navigation.events' }),
      onClick: () =>
        handleButtonClick('events', () => {
          window.location.href = headerLinks['events'];
        }),
    },
    {
      key: 'contacts',
      label: intl.formatMessage({ id: 'navigation.contacts' }),
      onClick: () =>
        handleButtonClick('contacts', () => {
          window.location.href = headerLinks['contacts'];
        }),
    },
  ];

  const emarketItems = [
    {
      key: 'sales',
      label: intl.formatMessage({ id: 'general.sales' }),
      onClick: () =>
        handleButtonClick('e-shop', () =>
          navigate(PublicRoute.PRODUCTS_ALL_DISCOUNTED)
        ),
    },
    {
      key: 'news',
      label: intl.formatMessage({ id: 'general.news' }),
      onClick: () =>
        handleButtonClick('e-shop', () => navigate(PublicRoute.PRODUCTS_ALL_NEW)),
    },
    {
      key: 'gift_sets',
      label: intl.formatMessage({ id: 'general.gift_sets' }),
      onClick: () =>
        handleButtonClick('e-shop', () => {
          navigate(publicPath.giftSets());
        }),
    },
  ];

  const wholesaleItems = [
    {
      key: 'listings',
      label: intl.formatMessage({ id: 'general.listings' }),
      onClick: () =>
        handleButtonClick('listings', () =>
          navigate(PublicRoute.WHOLESALE_LISTINGS)
        ),
    },
    {
      key: 'producers',
      label: intl.formatMessage({ id: 'general.producers' }),
      onClick: () =>
        handleButtonClick('producers', () => {
          window.location.href = PublicRoute.WHOLESALE_FARMS_ALL;
        }),
    },
    {
      key: 'sales',
      label: intl.formatMessage({ id: 'general.sales' }),
      onClick: () =>
        handleButtonClick('sales', () =>
          navigate(PublicRoute.WHOLESALE_PRODUCTS_ALL_DISCOUNTED)
        ),
    },
  ];

  const items = [
    ...sharedItems,
    ...(isEmarket ? emarketItems : wholesaleItems),
  ];

  const openCloseDrawer = () => {
    const shouldOpenMenu = !isMenuOpen && !isOpenMobileFilter && !openProfile;

    if (shouldOpenMenu) {
      setIsMenuOpen(true);
      setOpenProfile(false);
      dispatchFilter({
        type: 'SAVE_PAYLOAD',
        payload: {
          isOpenMobileFilter: false,
          isOpenMobileMenu: true,
        },
      });
    } else {
      setIsMenuOpen(false);
      setOpenProfile(false);
      dispatchFilter({
        type: 'SAVE_PAYLOAD',
        payload: {
          isOpenMobileFilter: false,
          isOpenMobileMenu: false,
        },
      });
    }
  };

  const openCloseProfileDrawer = () => {
    if (!openProfile) {
      setOpenProfile(true);
      setIsMenuOpen(false);
      dispatchFilter({
        type: 'SAVE_PAYLOAD',
        payload: { isOpenMobileMenu: false },
      });
    } else {
      setOpenProfile(false);
    }
  };

  const openCloseLoginDrawer = () => {
    setIsMenuOpen(false);
    setOpenProfile(false);

    dispatchFilter({
      type: 'SAVE_PAYLOAD',
      payload: { isOpenMobileMenu: false },
    });

    navigate(PublicRoute.MOBILE_AUTH_ENTRY);
  };

  return (
    <>
      <StyledMobileMenu>
        <div className="mobile-menu-container">
          <div className="mobile-menu-content">
            <div
              className={`mobile-menu-item ${selectedKey === 'profile' ? 'selected' : ''}`}
            >
              <Button
                type="text"
                className={selectedKey === 'profile' ? 'selected' : ''}
                label={intl.formatMessage({ id: 'general.profile' })}
                icon={<Icon name="profile-icon" />}
                onClick={() =>
                  handleButtonClick('profile', () => {
                    if (user?.authenticated) {
                      setOpenProfile((prev) => !prev);
                      setIsMenuOpen(false);
                      dispatchFilter({
                        type: 'SAVE_PAYLOAD',
                        payload: {
                          isOpenMobileFilter: false,
                          isOpenMobileMenu: false,
                        },
                      });
                    } else {
                      openCloseLoginDrawer();
                    }
                  })
                }
              />
            </div>

            <div
              className={`mobile-menu-item ${selectedKey === 'e-shop' ? 'selected' : ''}`}
            >
              <Button
                type="text"
                className={selectedKey === 'e-shop' ? 'selected' : ''}
                label={intl.formatMessage({
                  id: isEmarket ? 'general.e-shop' : 'general.products',
                })}
                icon={<Icon name="e-shop-icon" />}
                onClick={() =>
                  handleButtonClick('e-shop', () =>
                    navigate(
                      isEmarket
                        ? PublicRoute.PRODUCTS_ALL
                        : PublicRoute.WHOLESALE_PRODUCTS_ALL
                    )
                  )
                }
              />
            </div>
          </div>

          <div className="mobile-menu-filter">
            <Button
              type="text"
              icon={
                isOpenMobileFilter || openProfile || isMenuOpen ? (
                  <Icon
                    name={
                      isEmarket
                        ? 'mobile-filter-close-button-icon'
                        : 'mobile-filter-wholesale-close-button-icon'
                    }
                    type="filter-icon"
                  />
                ) : (
                  <Icon
                    name={
                      isEmarket
                        ? 'mobile-menu-open-icon'
                        : 'mobile-menu-wholesale-open-icon'
                    }
                    type="filter-icon"
                  />
                )
              }
              onClick={() => openCloseDrawer()}
            />
          </div>

          <div className="mobile-menu-content">
            <div
              className={`mobile-menu-item manufacturers ${selectedKey === 'producers' ? 'selected' : ''}`}
            >
              <Button
                type="text"
                className={selectedKey === 'producers' ? 'selected' : ''}
                label={intl.formatMessage({
                  id: isEmarket ? 'general.producers' : 'general.listings',
                })}
                icon={<Icon name="producers-icon" />}
                onClick={() =>
                  handleButtonClick('producers', () =>
                    navigate(
                      isEmarket
                        ? PublicRoute.FARMS_ALL
                        : PublicRoute.WHOLESALE_LISTINGS
                    )
                  )
                }
              />
            </div>

            {(isEmarket || !user?.authenticated || user?.farmId) && (
              <div
                className={`mobile-menu-item ${selectedKey === 'cart' ? 'selected' : ''}`}
              >
                <div className={'mobile-menu-cart-total'}>
                  {priceTotal.toFixed(2) + ' €'}
                </div>
                <Button
                  type="text"
                  className={selectedKey === 'cart' ? 'selected' : ''}
                  label={intl.formatMessage({ id: 'general.basket' })}
                  icon={<Icon name="cart-icon" />}
                  onClick={() => {
                    if (!isEmarket && !user?.authenticated) {
                      openCloseLoginDrawer();
                      return;
                    }
                    if (farmProducts.length === 0) {
                      message.error(
                        intl.formatMessage({
                          id: 'cart_summary.empty_warning',
                        })
                      );

                      return;
                    }
                    handleButtonClick('cart', () =>
                      navigate(isEmarket ? 'cart' : PublicRoute.WHOLESALE_CART)
                    );
                  }}
                />
              </div>
            )}
          </div>
        </div>

        <MobileMenuDrawer
          onClose={openCloseDrawer}
          open={isMenuOpen}
          closable={false}
          className={'mobile-menu'}
        >
          <div className="market-type-switch">
            <button
              className={`market-switch-btn emarket ${isEmarket ? 'active' : ''}`}
              onClick={() => {
                window.location.href = headerLinks['market'];
              }}
            >
              {intl.formatMessage({ id: 'general.e-market' })}
            </button>
            <button
              className={`market-switch-btn wholesale ${!isEmarket ? 'active' : ''}`}
              onClick={() => {
                window.location.href = PublicRoute.WHOLESALE_PRODUCTS_ALL;
              }}
            >
              {intl.formatMessage({ id: 'general.wholesale_header' })}
            </button>
          </div>
          <Menu mode="vertical" defaultSelectedKeys={['2']} items={items} />
        </MobileMenuDrawer>

        <MyProfileDrawer
          closable={false}
          onClose={openCloseProfileDrawer}
          open={openProfile}
          className={'mobile-menu'}
          title={intl.formatMessage({ id: 'general.my_profile' })}
        >
          <MobileProfielOptions
            orderCount={orderCount}
            openCloseProfileDrawer={openCloseProfileDrawer}
          />
        </MyProfileDrawer>
      </StyledMobileMenu>
    </>
  );
};

export default MobileMenu;
